import { useState } from 'react'
import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Dropdown from '../../components/ui/Dropdown'
import { getDaysUntil } from '../../lib/format'

const RECURRENCE_LABELS = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly',
}

function ProgressBar({ completed, total }) {
  if (total === 0) return null
  const percentage = Math.round((completed / total) * 100)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
      <div
        style={{
          flex: 1,
          height: '4px',
          background: 'var(--gray-200)',
          borderRadius: '2px',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${percentage}%`,
            height: '100%',
            background: percentage === 100 ? 'var(--success)' : 'var(--primary)',
            borderRadius: '2px',
            transition: 'width 0.2s ease',
          }}
        />
      </div>
      <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)', minWidth: '2.5rem' }}>
        {completed}/{total}
      </span>
    </div>
  )
}

function SubtaskItem({ subtask, onToggle, users }) {
  const assignee = subtask.assigned_to ? users.find(u => u.id === subtask.assigned_to) : null

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.375rem 0',
        borderRadius: '4px',
        cursor: 'pointer',
      }}
      onClick={() => onToggle(subtask.id)}
    >
      <div
        style={{
          width: '16px',
          height: '16px',
          borderRadius: '3px',
          border: subtask.is_completed
            ? '2px solid var(--success)'
            : '2px solid var(--gray-300)',
          background: subtask.is_completed ? 'var(--success)' : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          transition: 'all 0.15s ease',
        }}
      >
        {subtask.is_completed && (
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <span
        style={{
          flex: 1,
          fontSize: '0.8125rem',
          color: subtask.is_completed ? 'var(--gray-400)' : 'var(--gray-700)',
          textDecoration: subtask.is_completed ? 'line-through' : 'none',
          transition: 'color 0.15s ease',
        }}
      >
        {subtask.title}
      </span>
      {assignee && (
        <span
          title={assignee.name || assignee.email}
          style={{
            width: '18px',
            height: '18px',
            borderRadius: '50%',
            background: 'var(--gray-200)',
            color: 'var(--gray-600)',
            fontSize: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {getInitials(assignee.name || assignee.email)}
        </span>
      )}
    </div>
  )
}

function TodoCard({ todo, onEdit, onDelete, onToggle, onToggleSubtask, onAddSubtask, users = [] }) {
  const [newSubtask, setNewSubtask] = useState('')
  const [isAddingSubtask, setIsAddingSubtask] = useState(false)

  const isCompleted = todo.status === 'completed'
  const daysUntil = todo.due_date ? getDaysUntil(todo.due_date) : null

  const priorityStyles = {
    high: { background: 'var(--error)', label: 'High' },
    medium: { background: 'var(--warning)', label: 'Med' },
    low: { background: 'var(--success)', label: 'Low' },
  }

  const getDueDateLabel = () => {
    if (daysUntil === null) return null
    if (daysUntil < 0) return `${Math.abs(daysUntil)}d overdue`
    if (daysUntil === 0) return 'Today'
    if (daysUntil === 1) return 'Tomorrow'
    if (daysUntil <= 7) return `${daysUntil}d`
    return `${Math.ceil(daysUntil / 7)}w`
  }

  const assignee = todo.assigned_to ? users.find(u => u.id === todo.assigned_to) : null
  const subtasks = (todo.todo_subtasks || []).sort((a, b) => (a.order_position || 0) - (b.order_position || 0))
  const links = todo.todo_links || []
  const subtaskCompletedCount = subtasks.filter(s => s.is_completed).length

  const getInitials = (name) => {
    if (!name) return '?'
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const handleAddSubtask = (e) => {
    e.preventDefault()
    if (!newSubtask.trim()) return
    onAddSubtask(todo.id, newSubtask.trim())
    setNewSubtask('')
  }

  const dueDateColor = daysUntil !== null && daysUntil < 0
    ? 'var(--error)'
    : daysUntil !== null && daysUntil <= 1
      ? 'var(--warning)'
      : 'var(--gray-500)'

  return (
    <Card
      className={`todo-card ${isCompleted ? 'completed' : ''}`}
      style={{
        opacity: isCompleted ? 0.7 : 1,
        transition: 'opacity 0.2s ease',
      }}
    >
      {/* Main task row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        {/* Checkbox */}
        <button
          onClick={() => onToggle(todo.id)}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            border: isCompleted ? '2px solid var(--success)' : '2px solid var(--gray-300)',
            background: isCompleted ? 'var(--success)' : 'transparent',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            marginTop: '2px',
            transition: 'all 0.15s ease',
          }}
        >
          {isCompleted && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row with metadata */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <h4
              style={{
                margin: 0,
                fontSize: '0.9375rem',
                fontWeight: 500,
                color: isCompleted ? 'var(--gray-400)' : 'var(--gray-900)',
                textDecoration: isCompleted ? 'line-through' : 'none',
              }}
            >
              {todo.title}
            </h4>

            {/* Priority pill */}
            <span
              style={{
                fontSize: '0.625rem',
                fontWeight: 500,
                padding: '0.125rem 0.375rem',
                borderRadius: '9999px',
                background: `${priorityStyles[todo.priority]?.background}20`,
                color: priorityStyles[todo.priority]?.background,
                textTransform: 'uppercase',
                letterSpacing: '0.025em',
              }}
            >
              {priorityStyles[todo.priority]?.label}
            </span>

            {/* Due date */}
            {todo.due_date && (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: dueDateColor,
                  fontWeight: daysUntil !== null && daysUntil <= 1 ? 500 : 400,
                }}
              >
                {getDueDateLabel()}
              </span>
            )}

            {/* Recurring badge */}
            {todo.is_recurring && (
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                ↻ {RECURRENCE_LABELS[todo.recurrence_pattern] || ''}
              </span>
            )}
          </div>

          {/* Description */}
          {todo.description && (
            <p style={{
              margin: '0.25rem 0 0 0',
              fontSize: '0.8125rem',
              color: 'var(--gray-500)',
              lineHeight: 1.4,
            }}>
              {todo.description}
            </p>
          )}

          {/* Metadata row */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginTop: '0.5rem',
            flexWrap: 'wrap',
          }}>
            {/* Business */}
            {todo.businesses && (
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                {todo.businesses.emoji} {todo.businesses.name}
              </span>
            )}

            {/* Assignee */}
            {assignee && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  fontSize: '0.75rem',
                  color: 'var(--gray-500)',
                }}
              >
                <span
                  style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: 'var(--primary)',
                    color: 'white',
                    fontSize: '0.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {getInitials(assignee.name || assignee.email)}
                </span>
                {assignee.name || assignee.email?.split('@')[0]}
              </span>
            )}

            {/* Tags */}
            {todo.tags && todo.tags.length > 0 && (
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                {todo.tags.map(tag => (
                  <span
                    key={tag}
                    style={{
                      fontSize: '0.625rem',
                      padding: '0.125rem 0.375rem',
                      borderRadius: '4px',
                      background: 'var(--gray-100)',
                      color: 'var(--gray-600)',
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Links - shown as compact chips */}
          {links.length > 0 && (
            <div style={{ display: 'flex', gap: '0.375rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
              {links.map(link => (
                <a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    fontSize: '0.75rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    background: 'var(--primary-50, #EEF2FF)',
                    color: 'var(--primary)',
                    textDecoration: 'none',
                    transition: 'background 0.15s ease',
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                    <path d="M5 3H3a1 1 0 00-1 1v5a1 1 0 001 1h5a1 1 0 001-1V7m-3-3l5-2m0 0v3m0-3L7 5"
                      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {link.title || new URL(link.url).hostname.replace('www.', '')}
                </a>
              ))}
            </div>
          )}

          {/* Subtasks section - always visible */}
          {(subtasks.length > 0 || isAddingSubtask) && (
            <div style={{ marginTop: '0.75rem' }}>
              {/* Progress bar */}
              {subtasks.length > 0 && (
                <ProgressBar completed={subtaskCompletedCount} total={subtasks.length} />
              )}

              {/* Subtask list */}
              <div style={{ marginTop: '0.5rem', marginLeft: '-0.25rem' }}>
                {subtasks.map(subtask => (
                  <SubtaskItem
                    key={subtask.id}
                    subtask={subtask}
                    onToggle={onToggleSubtask}
                    users={users}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Add subtask input */}
          {isAddingSubtask ? (
            <form
              onSubmit={handleAddSubtask}
              style={{
                display: 'flex',
                gap: '0.5rem',
                marginTop: subtasks.length > 0 ? '0.25rem' : '0.75rem',
                marginLeft: '-0.25rem',
              }}
            >
              <input
                type="text"
                value={newSubtask}
                onChange={(e) => setNewSubtask(e.target.value)}
                placeholder="Add subtask..."
                autoFocus
                style={{
                  flex: 1,
                  padding: '0.375rem 0.5rem',
                  fontSize: '0.8125rem',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '4px',
                  outline: 'none',
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setIsAddingSubtask(false)
                    setNewSubtask('')
                  }
                }}
                onBlur={() => {
                  if (!newSubtask.trim()) {
                    setIsAddingSubtask(false)
                  }
                }}
              />
              <button
                type="submit"
                disabled={!newSubtask.trim()}
                style={{
                  padding: '0.375rem 0.75rem',
                  fontSize: '0.8125rem',
                  background: newSubtask.trim() ? 'var(--primary)' : 'var(--gray-200)',
                  color: newSubtask.trim() ? 'white' : 'var(--gray-400)',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: newSubtask.trim() ? 'pointer' : 'not-allowed',
                }}
              >
                Add
              </button>
            </form>
          ) : (
            <button
              onClick={() => setIsAddingSubtask(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                marginTop: subtasks.length > 0 ? '0.25rem' : '0.5rem',
                padding: '0.25rem 0',
                fontSize: '0.75rem',
                color: 'var(--gray-400)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'color 0.15s ease',
              }}
              onMouseOver={(e) => e.currentTarget.style.color = 'var(--gray-600)'}
              onMouseOut={(e) => e.currentTarget.style.color = 'var(--gray-400)'}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M6 3v6M3 6h6" strokeLinecap="round"/>
              </svg>
              Add subtask
            </button>
          )}
        </div>

        {/* Actions dropdown */}
        <Dropdown
          trigger={
            <button
              className="btn btn-ghost btn-sm"
              style={{
                padding: '0.25rem',
                opacity: 0.5,
                transition: 'opacity 0.15s ease',
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = 1}
              onMouseOut={(e) => e.currentTarget.style.opacity = 0.5}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="var(--gray-500)">
                <circle cx="8" cy="3" r="1.5"/>
                <circle cx="8" cy="8" r="1.5"/>
                <circle cx="8" cy="13" r="1.5"/>
              </svg>
            </button>
          }
        >
          <Dropdown.Item onClick={() => onEdit(todo)}>
            Edit task
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => onDelete(todo.id)} danger>
            Delete
          </Dropdown.Item>
        </Dropdown>
      </div>
    </Card>
  )
}

export default TodoCard
